import React, { forwardRef } from 'react'

import {
  isValidBsbAu,
  isValidInstitutiontNumberCa,
  isValidRoutingNumberGb,
  isValidRoutingNumberUsa,
} from '../../utils/validation'
import { ArcadeHelpTextComponentProps } from '../Arcade/Input/ArcadeHelpText'
import FormattedInput, { BaseFormattedInputProps } from '../Arcade/Input/FormattedInput'

export type RoutingNumberRegion = 'US' | 'CA' | 'GB' | 'AU'

export interface RoutingNumberInputProps extends BaseFormattedInputProps, ArcadeHelpTextComponentProps {
  /** The region that determines format and validation */
  region: RoutingNumberRegion
}

export const routingNumberRegionConfig = {
  AU: {
    format: '###-###',
    validate: isValidBsbAu,
  },
  CA: {
    format: '###',
    validate: isValidInstitutiontNumberCa,
  },
  GB: {
    format: '##-##-##',
    validate: isValidRoutingNumberGb,
  },
  US: {
    format: '#########',
    validate: isValidRoutingNumberUsa,
  },
} as const

/**
 * A formatted input for bank routing numbers with region-specific formatting and validation.
 * Supports US (routing number), CA (institution number), GB (sort code), and AU (BSB number) formats.
 *
 * @example
 * ```tsx
 * <FieldLabel label="Routing Number">
 *   <RoutingNumberInput
 *     region="US"
 *     value={value}
 *     onChange={setValue}
 *     placeholder="Enter your routing number"
 *   />
 * </FieldLabel>
 * ```
 */
const RoutingNumberInput = forwardRef<HTMLInputElement, RoutingNumberInputProps>(
  ({ region, placeholder, ...rest }, ref) => {
    const { format } = routingNumberRegionConfig[region]

    return <FormattedInput ref={ref} template={format} placeholder={placeholder || format} {...rest} />
  }
)

export default RoutingNumberInput
